/** Display formatting. Kept in one place so tiles, tooltips and tables agree. */

const METRIC_LABELS: Record<string, string> = {
  users: 'Users',
  sessions: 'Sessions',
  page_views: 'Page views',
  engaged_sessions: 'Engaged sessions',
  engagement_rate: 'Engagement rate',
  sessions_per_user: 'Sessions per user',
  transactions: 'Transactions',
  revenue: 'Revenue',
  conversion_rate: 'Conversion rate',
  event_count: 'Event count',
  // The paywall trend plots raw event names, because that is what its series are keyed
  // on — the backend has no metric to map them onto.
  paywall_view: 'Paywall views',
  paywall_click_abbonati: 'Subscribe clicks',
  paywall_click_accedi: 'Log-in clicks',
}

export function metricLabel(metric: string): string {
  return METRIC_LABELS[metric] ?? metric.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase())
}

const CURRENCY_METRICS = new Set(['revenue'])
const PERCENT_METRICS = new Set(['conversion_rate', 'engagement_rate'])
/** Small ratios that would read as "0" under the compact integer formatter. */
const RATIO_METRICS = new Set(['sessions_per_user'])

/** Compact form for stat tiles: 1,284 / 12.9K / 4.2M. */
export function compact(value: number): string {
  const magnitude = Math.abs(value)
  if (magnitude >= 1_000_000) return `${trim(value / 1_000_000)}M`
  if (magnitude >= 10_000) return `${trim(value / 1_000)}K`
  return value.toLocaleString(undefined, { maximumFractionDigits: 0 })
}

function trim(value: number): string {
  return value.toFixed(1).replace(/\.0$/, '')
}

export function formatMetric(metric: string, value: number, style: 'compact' | 'full' = 'compact'): string {
  if (PERCENT_METRICS.has(metric)) return `${value.toFixed(1)}%`
  if (RATIO_METRICS.has(metric)) return value.toFixed(2)
  if (CURRENCY_METRICS.has(metric)) {
    return style === 'compact'
      ? `€${compact(value)}`
      : `€${value.toLocaleString(undefined, { maximumFractionDigits: 2 })}`
  }
  return style === 'compact' ? compact(value) : value.toLocaleString(undefined, { maximumFractionDigits: 0 })
}

export function formatChange(percent: number): string {
  return `${percent > 0 ? '+' : ''}${percent.toFixed(1)}%`
}

/**
 * Axis-tick variant: same formatting, minus a trailing `.0`.
 *
 * A tile value earns its decimal because the exact figure is the point; an axis tick
 * is a reference gridline, and "20%" reads faster than "20.0%".
 */
export function formatAxisTick(metric: string, value: number): string {
  return formatMetric(metric, value).replace(/\.0(?=%?$)/, '')
}

/**
 * Format an ISO date without constructing a `Date`.
 *
 * `new Date('2026-08-10')` parses as UTC midnight and renders as the 9th for anyone
 * west of Greenwich. GA4 dates are already calendar dates in the property's timezone,
 * so they are formatted from their parts.
 */
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

export function formatDate(iso: string, style: 'short' | 'long' = 'short'): string {
  const [year, month, day] = iso.split('-').map(Number)
  if (!year || !month || !day) return iso
  const monthName = MONTHS[month - 1] ?? ''
  return style === 'long' ? `${day} ${monthName} ${year}` : `${day} ${monthName}`
}

export function formatRange(start: string, end: string): string {
  return start === end ? formatDate(start, 'long') : `${formatDate(start)} – ${formatDate(end, 'long')}`
}

/** Whether an upward move is good. Only bounce-style metrics would invert this. */
export function upIsGood(_metric: string): boolean {
  return true
}

export function deltaTone(percent: number, metric: string): 'up' | 'down' | 'flat' {
  if (percent === 0) return 'flat'
  const rising = percent > 0
  return rising === upIsGood(metric) ? 'up' : 'down'
}
